import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { useEffect, useState } from "react";
import type { ListParams } from "@/services/licitaciones.service";
import { licitacionesService, type Facets } from "@/services/licitaciones.service";
import type { LicitacionEstado, Prioridad } from "@/types";
import {
  CANALES,
  CANALES_SIN_LICITACIONES,
  ESTADOS_MP,
  TIPOS_MP,
  canalDeTipo,
  codigosDeCanal,
  estadoMPLabel,
  type CanalId,
} from "@/lib/mercado-publico-catalog";


const ESTADOS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "detectada", label: "Detectada" },
  { value: "analisis", label: "En análisis" },
  { value: "viable", label: "Viable" },
  { value: "preparacion", label: "Preparación" },
  { value: "postulada", label: "Postulada" },
  { value: "evaluacion", label: "Evaluación" },
  { value: "adjudicada", label: "Adjudicada" },
  { value: "perdida", label: "Perdida" },
  { value: "cerrada", label: "Cerrada" },
];

const PRIORIDADES: { value: string; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
];

export interface FiltersState {
  estado: string;
  estadoMP: string;
  prioridad: string;
  responsable: string;
  region: string;
  organismo: string;
  categoria: string;
  canal: string;
  tipoMP: string;
  fechaDesde: string;
  fechaHasta: string;
  montoMin: string;
  montoMax: string;
  minScore: number;
}

const defaultFilters: FiltersState = {
  estado: "",
  estadoMP: "",
  prioridad: "",
  responsable: "",
  region: "",
  organismo: "",
  categoria: "",
  canal: "",
  tipoMP: "",
  fechaDesde: "",
  fechaHasta: "",
  montoMin: "",
  montoMax: "",
  minScore: 0,
};

/** Deduce el canal a partir del tipoMP (o de la lista de códigos) del filtro actual. */
function canalDesdeParams(tipoMP?: string): { canal: string; tipo: string } {
  if (!tipoMP) return { canal: "", tipo: "" };
  const codigos = tipoMP.split(",").map((c) => c.trim()).filter(Boolean);
  if (codigos.length === 1) {
    return { canal: canalDeTipo(codigos[0]) ?? "", tipo: codigos[0] };
  }
  const canal = CANALES.find(
    (c) =>
      codigosDeCanal(c.id).length === codigos.length &&
      codigosDeCanal(c.id).every((code) => codigos.includes(code)),
  );
  return { canal: canal?.id ?? "", tipo: "" };
}

interface FiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: Partial<ListParams>;
  onApply: (params: Partial<ListParams>) => void;
}

export function FiltersSheet({ open, onOpenChange, current, onApply }: FiltersSheetProps) {
  const inicial = canalDesdeParams(current.tipoMP);
  const [f, setF] = useState<FiltersState>(() => ({
    estado: current.estado ?? "",
    estadoMP: current.estadoMP ?? "",
    prioridad: current.prioridad ?? "",
    responsable: current.responsable ?? "",
    region: current.region ?? "",
    organismo: current.organismo ?? "",
    categoria: current.categoria ?? "",
    canal: inicial.canal,
    tipoMP: inicial.tipo,
    fechaDesde: current.fechaDesde ?? "",
    fechaHasta: current.fechaHasta ?? "",
    montoMin: current.montoMin != null ? String(current.montoMin) : "",
    montoMax: current.montoMax != null ? String(current.montoMax) : "",
    minScore: current.minScore ?? 0,
  }));

  const [facets, setFacets] = useState<Facets | null>(null);

  useEffect(() => {
    if (!open || facets) return;
    let cancelled = false;
    licitacionesService
      .facets()
      .then((data) => {
        if (!cancelled) setFacets(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, facets]);

  const set = (key: keyof FiltersState, value: string | number) =>
    setF((prev) => ({ ...prev, [key]: value }));

  const countTipo = (codigo: string) =>
    facets?.tipoMP.find((t) => t.value === codigo)?.count ?? null;
  const countEstadoMP = (value: string) =>
    facets?.estadoMP.find((e) => e.value === value)?.count ?? null;

  /**
   * Tipos disponibles según el canal elegido; oculta los sin datos si hay
   * facetas y los canales que MP no publica en `licitaciones.json`.
   */
  const tiposVisibles = TIPOS_MP.filter((t) => !CANALES_SIN_LICITACIONES.includes(t.canal))
    .filter((t) => !f.canal || t.canal === (f.canal as CanalId))
    .filter((t) => !facets || countTipo(t.codigo) !== null);


  const handleApply = () => {
    const params: Partial<ListParams> = {};
    if (f.estado) params.estado = f.estado as LicitacionEstado;
    if (f.estadoMP) params.estadoMP = f.estadoMP;
    if (f.prioridad) params.prioridad = f.prioridad as Prioridad;
    if (f.responsable) params.responsable = f.responsable;
    if (f.region) params.region = f.region;
    if (f.organismo) params.organismo = f.organismo;
    if (f.categoria) params.categoria = f.categoria;
    if (f.tipoMP) params.tipoMP = f.tipoMP;
    else if (f.canal) params.tipoMP = codigosDeCanal(f.canal as CanalId).join(",");
    if (f.fechaDesde) params.fechaDesde = f.fechaDesde;
    if (f.fechaHasta) params.fechaHasta = f.fechaHasta;
    if (f.montoMin) params.montoMin = Number(f.montoMin);
    if (f.montoMax) params.montoMax = Number(f.montoMax);
    if (f.minScore > 0) params.minScore = f.minScore;
    onApply(params);
    onOpenChange(false);
  };

  const handleClear = () => {
    setF(defaultFilters);
    onApply({});
    onOpenChange(false);
  };

  const Section = ({
    title,
    hint,
    children,
  }: {
    title: string;
    hint?: string;
    children: React.ReactNode;
  }) => (
    <div className="space-y-2">
      <h4 className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
        {title}
      </h4>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground">{hint}</p> : null}
    </div>
  );

  const SelectRow = ({
    value,
    onChange,
    options,
    ariaLabel,
  }: {
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string; disabled?: boolean; title?: string }[];
    ariaLabel: string;
  }) => (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled} title={o.title}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Filtros avanzados</SheetTitle>
        </SheetHeader>

        <div className="space-y-5">
          <Section
            title="Canal de compra"
            hint="Convenio Marco no se publica en el listado de licitaciones de Mercado Público: revísalo en Órdenes de compra."
          >
            <SelectRow
              ariaLabel="Canal de compra"
              value={f.canal}
              onChange={(v) => {
                set("canal", v);
                set("tipoMP", "");
              }}
              options={[
                { value: "", label: "Todos los canales" },
                ...CANALES.map((c) => {
                  const sinDatos = CANALES_SIN_LICITACIONES.includes(c.id);
                  return {
                    value: c.id,
                    label: sinDatos ? `${c.label} (no disponible aquí)` : c.label,
                    disabled: sinDatos,
                    title: sinDatos
                      ? "Mercado Público no publica procesos de Convenio Marco en el endpoint de licitaciones; revísalos en Órdenes de compra."
                      : undefined,
                  };
                }),
              ]}
            />
          </Section>


          <Section title="Tipo de licitación (código MP)">
            <SelectRow
              ariaLabel="Tipo de licitación"
              value={f.tipoMP}
              onChange={(v) => set("tipoMP", v)}
              options={[
                { value: "", label: f.canal ? "Todos del canal" : "Todos" },
                ...tiposVisibles.map((t) => {
                  const n = countTipo(t.codigo);
                  return {
                    value: t.codigo,
                    label: `${t.codigo} — ${t.descripcion}${n != null ? ` (${n})` : ""}`,
                  };
                }),
              ]}
            />
          </Section>

          <Section title="Estado en Mercado Público">
            <SelectRow
              ariaLabel="Estado en Mercado Público"
              value={f.estadoMP}
              onChange={(v) => set("estadoMP", v)}
              options={[
                { value: "", label: "Todos" },
                ...(facets && facets.estadoMP.length > 0
                  ? facets.estadoMP.map((e) => ({
                      value: e.value,
                      label: `${estadoMPLabel(e.value)} (${e.count})`,
                    }))
                  : ESTADOS_MP.map((e) => ({ value: e.value, label: e.label }))),
              ]}
            />
          </Section>

          <Section title="Pipeline (estado interno)">
            <SelectRow
              ariaLabel="Pipeline"
              value={f.estado}
              onChange={(v) => set("estado", v)}
              options={ESTADOS}
            />
          </Section>

          <Section title="Prioridad">
            <SelectRow
              ariaLabel="Prioridad"
              value={f.prioridad}
              onChange={(v) => set("prioridad", v)}
              options={PRIORIDADES}
            />
          </Section>

          <Section title="Responsable">
            <Input
              value={f.responsable}
              onChange={(e) => set("responsable", e.target.value)}
              placeholder="Nombre del responsable..."
              className="h-8 text-sm"
            />
          </Section>

          <Section title="Región">
            <Input
              list="facet-regiones"
              value={f.region}
              onChange={(e) => set("region", e.target.value)}
              placeholder="Ej: Metropolitana"
              className="h-8 text-sm"
            />
            <datalist id="facet-regiones">
              {facets?.region.map((r) => (
                <option key={r.value} value={r.value}>
                  {`${r.value} (${r.count})`}
                </option>
              ))}
            </datalist>
          </Section>

          <Section title="Organismo">
            <Input
              list="facet-organismos"
              value={f.organismo}
              onChange={(e) => set("organismo", e.target.value)}
              placeholder="Ej: MOP, MINSAL..."
              className="h-8 text-sm"
            />
            <datalist id="facet-organismos">
              {facets?.organismo.map((o) => (
                <option key={o.value} value={o.value}>
                  {`${o.value} (${o.count})`}
                </option>
              ))}
            </datalist>
          </Section>

          <Section title="Categoría">
            <Input
              value={f.categoria}
              onChange={(e) => set("categoria", e.target.value)}
              placeholder="Ej: Tecnología y Software"
              className="h-8 text-sm"
            />
          </Section>

          <Section title="Fecha de cierre">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Desde</Label>
                <Input
                  type="date"
                  value={f.fechaDesde}
                  onChange={(e) => set("fechaDesde", e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Hasta</Label>
                <Input
                  type="date"
                  value={f.fechaHasta}
                  onChange={(e) => set("fechaHasta", e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </Section>

          <Section title="Monto estimado">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Mínimo ($)</Label>
                <Input
                  type="number"
                  min={0}
                  value={f.montoMin}
                  onChange={(e) => set("montoMin", e.target.value)}
                  placeholder="0"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Máximo ($)</Label>
                <Input
                  type="number"
                  min={0}
                  value={f.montoMax}
                  onChange={(e) => set("montoMax", e.target.value)}
                  placeholder="Sin límite"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </Section>

          <Section title="Score IA mínimo">
            <div className="flex items-center gap-3">
              <Slider
                value={[f.minScore]}
                onValueChange={([v]) => set("minScore", v)}
                min={0}
                max={100}
                step={5}
                className="flex-1"
              />
              <span className="text-sm font-mono tabular-nums text-foreground w-8 text-right">
                {f.minScore}
              </span>
            </div>
          </Section>
        </div>

        <div className="flex items-center gap-2 mt-8 pt-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={handleClear} className="flex-1">
            Limpiar
          </Button>
          <Button size="sm" onClick={handleApply} className="flex-1">
            Aplicar filtros
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
